import { create } from "zustand";
import { parseFeaturesListResponse } from "../utils/aiFeaturesCatalogUtils";

export const hasAiFeaturesCatalogContext = (appProperties) =>
    Boolean(
        appProperties?.isAiFeaturesEnabled &&
            appProperties?.osyncId &&
            appProperties?.companyId &&
            appProperties?.userId
    );

let catalogLoadPromise = null;

/** Unwrap fetchData's Axios envelope: rawResponse → { data: apiBody } → apiBody may itself have { data: [...] }. */
const parseFeaturesListApiResponse = (rawResponse) => {
    if (!rawResponse) {
        return [];
    }
    const responseData = typeof rawResponse === "string" ? JSON.parse(rawResponse) : rawResponse;
    const body = responseData?.data;
    if (Array.isArray(body)) {
        return parseFeaturesListResponse(body);
    }
    if (body && Array.isArray(body.data)) {
        return parseFeaturesListResponse(body.data);
    }
    return parseFeaturesListResponse(body);
};

const parseUpdateFeatureApiResponse = (rawResponse) => {
    if (!rawResponse) {
        return null;
    }
    const responseData = typeof rawResponse === "string" ? JSON.parse(rawResponse) : rawResponse;
    return responseData?.data ?? null;
};

export const useAiFeaturesStore = create((set, get) => ({
    catalog: [],
    catalogLoaded: false,
    catalogLoading: false,
    catalogError: null,

    clearCatalog: () => {
        catalogLoadPromise = null;
        set({
            catalog: [],
            catalogLoaded: false,
            catalogLoading: false,
            catalogError: null,
        });
    },

    /** Lightweight enabled status for all users via GET /ai/features/enabled. */
    fetchCatalog: async (fetchData, appProperties) => {
        if (!hasAiFeaturesCatalogContext(appProperties)) {
            return get().catalog;
        }
        if (!fetchData) {
            set({ catalogError: "fetchData function is required" });
            return get().catalog;
        }

        set({ catalogLoading: true, catalogError: null });

        try {
            const rawResponse = await fetchData("ai/features/enabled", "GET", null, appProperties);
            if (!rawResponse) {
                set({
                    catalogError: "Unable to load AI features",
                    catalogLoading: false,
                });
                return get().catalog;
            }
            const nextCatalog = parseFeaturesListApiResponse(rawResponse);
            set({ catalog: nextCatalog, catalogLoaded: true, catalogLoading: false, catalogError: null });
            return nextCatalog;
        } catch (error) {
            console.error("Failed to fetch AI features catalog:", error);
            set({
                catalogError: error?.message || "Failed to load AI features",
                catalogLoading: false,
            });
            throw error;
        } finally {
            catalogLoadPromise = null;
        }
    },

    ensureCatalogLoaded: async (fetchData, appProperties) => {
        if (!hasAiFeaturesCatalogContext(appProperties)) {
            return get().catalog;
        }
        const { catalogLoaded, catalog } = get();
        if (catalogLoaded) {
            return catalog;
        }
        if (catalogLoadPromise) {
            return catalogLoadPromise;
        }
        catalogLoadPromise = get()
            .fetchCatalog(fetchData, appProperties)
            .catch(() => get().catalog);
        return catalogLoadPromise;
    },

    refreshCatalog: async (fetchData, appProperties) => {
        catalogLoadPromise = null;
        set({ catalogLoaded: false });
        return get().fetchCatalog(fetchData, appProperties);
    },

    updateFeatureEnabled: async (fetchData, appProperties, featureFlag, enabled) => {
        if (!fetchData || !appProperties?.osyncId) {
            return { success: false, message: "Missing app context for AI feature update" };
        }

        const previousCatalog = get().catalog;
        set({
            catalog: previousCatalog.map((featureRow) =>
                Number(featureRow.featureFlag) === featureFlag ? { ...featureRow, enabled } : featureRow
            ),
        });

        try {
            const rawResponse = await fetchData(
                "ai/addon-features",
                "PUT",
                { featureFlag, enabled },
                appProperties
            );
            const toggleResponse = parseUpdateFeatureApiResponse(rawResponse);

            if (toggleResponse?.status === "true") {
                set({ catalogLoaded: true });
                return {
                    success: true,
                    message: typeof toggleResponse?.message === "string" ? toggleResponse.message : undefined,
                };
            }

            set({ catalog: previousCatalog });
            return {
                success: false,
                message:
                    typeof toggleResponse?.message === "string"
                        ? toggleResponse.message
                        : "Could not update feature",
            };
        } catch (error) {
            console.error("Failed to update AI feature:", error);
            set({ catalog: previousCatalog });
            return {
                success: false,
                message: error?.message || "Could not update feature",
            };
        }
    },
}));
